import {
  FileInfoDto,
  GDTMessageDirection,
  GDTMessageDto,
} from '@lmt/shared-types';

import styles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { ScrollTableOnScreenToTop } from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useCallback, useEffect } from 'react';
import { GDTMessageInfo } from './common';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import { HL7DocumentItem } from '../../../hl7/messages/components/DocumentItem';
import { getFullName } from '../../../patients/patient-form/common/common';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import localStyles from '../GDTMessagesPage.module.scss';
import {
  DXG,
  useDataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';

function dateToFormat(date: Date): string {
  return localFormat(date, 'Pp');
}

function getFormattedDateTime(date?: Date | null): string {
  return date ? dateToFormat(date) : '';
}

type Props = {
  noResults: boolean;
  data: GDTMessageDto[];
  documents: FileInfoDto[];
  onDelete: (id: number) => void;
  onSave: (message: GDTMessageInfo) => void;
};

type RowData = {
  data: GDTMessageDto;
  value: any;
};

const nameof = nameofFactory<GDTMessageDto>();

export const GDTMessagesTable: FC<Props> = React.memo(
  function GDTMessagesTable(props) {
    useDataGrid();
    const i18n = useScopedTranslation('gdt.messages');
    const claims = useClaims();
    const tabs = useTabs();

    const openPatientProfile = useCallback((patientId: number) => {
      tabs.openTab(pages.patientProfile.link({ patientId }));
    }, []);

    useEffect(() => {
      ScrollTableOnScreenToTop();
    }, [props.data]);

    const RowDetail = useCallback((props: any) => {
      const data = props.data.data;
      const content: string = data.content.replace(/(\r\n|\r|\n)/g, '\r\n');
      return (
        <>
          <div className={styles.content}>{content}</div>
        </>
      );
    }, []);

    if (props.noResults) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>
            {i18n.t('empty_list_text')}
          </div>
        </div>
      );
    }

    return (
      <DXG.DataGrid
        keyExpr="id"
        dataSource={props.data ?? []}
        width={'100%'}
        height={'100%'}
      >
        <DXG.MasterDetail enabled={true} component={RowDetail} />
        <DXG.Paging enabled={false} />
        <DXG.Column
          width={60}
          alignment={'left'}
          dataField={nameof('id')}
          caption={i18n.t('columns.id')}
          allowSorting={false}
        />
        <DXG.Column
          width={155}
          alignment={'left'}
          dataField={nameof('dateTime')}
          caption={i18n.t('columns.date_time')}
          calculateDisplayValue={(message: GDTMessageDto) => {
            return getFormattedDateTime(message.dateTime);
          }}
          allowSorting={false}
        />
        <DXG.Column
          width={100}
          alignment={'left'}
          dataField={nameof('direction')}
          caption={i18n.t('columns.direction')}
          calculateDisplayValue={(message: GDTMessageDto) => {
            return message.direction === GDTMessageDirection.Incoming
              ? i18n.t('incoming_message_status')
              : i18n.t('outgoing_message_status');
          }}
          allowSorting={false}
        />
        <DXG.Column
          width={100}
          alignment={'left'}
          dataField={nameof('status')}
          caption={i18n.t('columns.status')}
          calculateDisplayValue={(message: GDTMessageDto) =>
            i18n.t(`status.${message.status}` as any)
          }
          allowSorting={false}
        />
        <DXG.Column
          width={200}
          alignment={'left'}
          dataField={nameof('patient')}
          caption={i18n.t('columns.patient')}
          cellRender={(celldata: RowData) => {
            return (
              <div
                onClick={(event: any) => {
                  event.stopPropagation();
                  celldata.data.patient &&
                    openPatientProfile(celldata.data.patient.id);
                }}
                className={styles.patientNameInfo}
              >
                {getFullName(celldata.value)}
              </div>
            );
          }}
          allowSorting={false}
        />
        <DXG.Column
          width={200}
          dataField={nameof('outgoingFileId')}
          caption={i18n.t('columns.document')}
          cellRender={(celldata: RowData) => {
            return (
              <div onClick={(event: any) => event.stopPropagation()}>
                <HL7DocumentItem
                  document={props.documents.find(
                    (d) => d.id === celldata.data.outgoingFileId,
                  )}
                />
              </div>
            );
          }}
          allowSorting={false}
        />
        <DXG.Column
          alignment={'left'}
          dataField={nameof('error')}
          caption={i18n.t('columns.error')}
          cellRender={(celldata: RowData) => {
            return (
              <div className={localStyles.error}>{celldata.data.error}</div>
            );
          }}
          allowSorting={false}
        />
        <DXG.Column
          width={50}
          alignment="right"
          caption={i18n.t('columns.actions')}
          cellRender={(celldata: RowData) => {
            return (
              <div onClick={(event: any) => event.stopPropagation()}>
                {claims.permissions.gdtManagement && (
                  <TableDotMenu
                    iconClassName={localStyles['option-button']}
                    options={[
                      {
                        title: i18n.t('options.save_file'),
                        onClick: () => props.onSave(celldata.data),
                      },
                      {
                        title: i18n.t('options.delete'),
                        onClick: () => props.onDelete(celldata.data.id),
                        isDeleteItem: true,
                        isUnderLine: true,
                      },
                    ]}
                  />
                )}
              </div>
            );
          }}
        />
      </DXG.DataGrid>
    );
  },
);
